import { useState } from "react";

const FORM_URL = "https://readdy.ai/api/form/dag45vbep053ijub23g0";

type Status = "idle" | "loading" | "success" | "error";

export default function SubscribeSection() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = String(fd.get("website_alt") || "").trim();

    if (honeypot) {
      setStatus("success");
      form.reset();
      setEmail("");
      return;
    }

    setStatus("loading");
    setFormError("");
    const body = new URLSearchParams();
    fd.forEach((value, key) => {
      if (key !== "website_alt" && value) body.append(key, String(value));
    });

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");

      if (res.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
        setEmail("");
      } else {
        setStatus("error");
        setFormError(isSpam ? "检测到异常提交，请稍后再试。" : serverMsg || "订阅失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试。");
    }
  };

  return (
    <section id="subscribe" className="w-full py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="relative overflow-hidden rounded-2xl bg-primary-700 px-6 py-10 md:px-12 md:py-14">
          <div className="pointer-events-none absolute -right-16 -top-16 w-64 h-64 rounded-full bg-background-50/10"></div>
          <div className="pointer-events-none absolute -bottom-20 left-1/3 w-72 h-72 rounded-full bg-accent-500/20 blur-2xl"></div>

          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="inline-flex items-center gap-1.5 text-accent-200 text-xs font-semibold tracking-widest">
                <i className="ri-mail-send-line"></i> MELON MORNING BRIEF
              </span>
              <h2 className="mt-2 font-heading text-2xl md:text-3xl font-extrabold text-background-50">
                订阅吃瓜早报，瓜田不迷路
              </h2>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-background-100 max-w-lg">
                每天清晨，为你整理前一日的恋情曝光、约会实锤、官宣与分手动态，配上简短复盘，几分钟看完最新瓜料。
              </p>
              <ul className="mt-5 flex flex-col gap-2 text-sm text-background-100">
                <li className="inline-flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-accent-300"></i> 每日精选 6–8 条真实瓜讯
                </li>
                <li className="inline-flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-accent-300"></i> 无广告打扰，随时可退订
                </li>
              </ul>
            </div>

            <div className="bg-background-50/95 rounded-xl p-6 md:p-7">
              <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-3">
                <input type="text" name="website_alt" className="honeypot-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                <div>
                  <label htmlFor="sub-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    常用邮箱
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      id="sub-email"
                      type="email"
                      name="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="flex-1 h-11 rounded-md border border-background-200 bg-background-50 px-3.5 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                    />
                    <button
                      type="submit"
                      disabled={status === "loading"}
                      className="whitespace-nowrap inline-flex items-center justify-center gap-1.5 rounded-md bg-primary-600 text-background-50 h-11 px-5 text-sm font-medium hover:bg-primary-700 disabled:opacity-60 transition-colors"
                    >
                      {status === "loading" ? (
                        <><i className="ri-loader-4-line animate-spin"></i> 提交中</>
                      ) : (
                        <><i className="ri-send-plane-line"></i> 立即订阅</>
                      )}
                    </button>
                  </div>
                </div>

                {status === "success" && (
                  <p className="text-sm text-secondary-700 inline-flex items-center gap-1.5">
                    <i className="ri-checkbox-circle-line"></i> 订阅成功，早报会准时送达你的邮箱！
                  </p>
                )}
                {status === "error" && (
                  <p className="text-sm text-primary-700 inline-flex items-center gap-1.5">
                    <i className="ri-error-warning-line"></i> {formError}
                  </p>
                )}
                <p className="text-[11px] leading-relaxed text-foreground-400">
                  提交即表示你同意接收绯闻瓜田的每日早报，我们不会向任何第三方分享你的邮箱。
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}